import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCopy,
  faGift,
  faHandshake,
  faShareNodes,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import styles from "./referidosPage.module.css";
import { referralService, formatCurrency } from "../../../../../services/api";
import { Badge, EmptyState, ErrorBanner, Spinner } from "../../../../../components/ui";
import { formatBonusLongDate } from "../../../../../utils/bonusRules";

const REFERRAL_STATUS_VARIANT = {
  PENDING: "pending",
  ACTIVE: "success",
  COMPLETED: "success",
  REWARDED: "success",
  EXPIRED: "default",
  CANCELLED: "error",
};

/** Enlace de registro con el código (la landing guarda el código para el registro). */
function referralLink(code) {
  if (typeof window === "undefined" || !code) return "";
  return `${window.location.origin}/auth?ref=${encodeURIComponent(code)}`;
}

function shareText(code) {
  return `¡Juega la lotería desde tu celular con Gold Loterías! Regístrate con mi código ${code} y con tu primera recarga te regalan una fracción gratis.`;
}

/**
 * "Referidos" en el perfil: mi código (copiar / compartir), cómo funciona el premio
 * y el resumen del programa (GET /discounts/referrals/dashboard/).
 * Usuario normal → 1 fracción gratis por cada amigo que haga su primera recarga.
 * Influencer → comisión sobre la primera recarga de sus referidos.
 */
export default function ReferidosPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    referralService.dashboard().then(async (res) => {
      if (cancelled) return;
      if (res.ok && res.data?.code) {
        setData(res.data);
        setLoading(false);
        return;
      }
      // Respaldo: al menos el código.
      const mine = await referralService.myCode();
      if (cancelled) return;
      if (mine.ok && mine.data?.code) {
        setData(mine.data);
      } else {
        setError(res.message || mine.message);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const copy = useCallback(async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      setCopied("");
    }
  }, []);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Cargando tu programa de referidos…" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <ErrorBanner
        message={error || "No pudimos cargar tu código de referido."}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  const code = data.code;
  const link = referralLink(code);
  const isInfluencer = data.is_influencer === true || data.reward_type === "COMMISSION";
  const rewards = data.free_fraction_rewards || {};
  const earned = Number(rewards.earned ?? data.free_fractions_earned ?? 0) || 0;
  const availableRewards = Number(rewards.available ?? 0) || 0;
  const usedRewards = Number(rewards.used ?? 0) || 0;
  const referrals = Array.isArray(data.referrals) ? data.referrals : [];
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleShare = async () => {
    try {
      await navigator.share({ title: "Gold Loterías", text: shareText(code), url: link });
    } catch {
      /* el usuario canceló */
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${shareText(code)} ${link}`)}`;

  return (
    <div className={styles.container}>
      <div className={styles.head}>
        <h2 className={styles.title}>Referidos</h2>
        {data.current_level?.name && <Badge variant="gold">Nivel {data.current_level.name}</Badge>}
      </div>

      <section className={styles.codeCard} aria-label="Tu código de referido">
        <p className={styles.codeLabel}>Tu código</p>
        <div className={styles.codeRow}>
          <span className={styles.codeValue}>{code}</span>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => copy(code, "code")}
            aria-label="Copiar código"
            title="Copiar código"
          >
            <FontAwesomeIcon icon={copied === "code" ? faCheck : faCopy} />
          </button>
        </div>
        <div className={styles.shareRow}>
          <button type="button" className={styles.shareBtn} onClick={() => copy(link, "link")}>
            <FontAwesomeIcon icon={copied === "link" ? faCheck : faCopy} />
            {copied === "link" ? "¡Enlace copiado!" : "Copiar enlace"}
          </button>
          <a
            className={styles.shareBtn}
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
          </a>
          {canShare && (
            <button type="button" className={styles.shareBtn} onClick={handleShare}>
              <FontAwesomeIcon icon={faShareNodes} /> Compartir
            </button>
          )}
        </div>
      </section>

      <section className={styles.howCard}>
        <span className={styles.howIcon} aria-hidden="true">
          <FontAwesomeIcon icon={isInfluencer ? faHandshake : faGift} />
        </span>
        <div>
          <h3 className={styles.howTitle}>
            {isInfluencer
              ? "Ganas comisión por tus referidos"
              : "Gana 1 fracción gratis por cada amigo"}
          </h3>
          {isInfluencer ? (
            <p className={styles.howText}>
              Como aliado de Gold Loterías recibes en tu saldo un porcentaje de la primera
              recarga de cada persona que se registre con tu código. Tus referidos también
              reciben el bono de bienvenida en su primera recarga.
            </p>
          ) : (
            <p className={styles.howText}>
              Comparte tu código. Cuando tu amigo se registre con él y haga su{" "}
              <strong>primera recarga desde $10.000</strong>, te regalamos{" "}
              <strong>1 fracción gratis</strong> para la lotería que quieras. Tu amigo también
              recibe su bono de bienvenida.
            </p>
          )}
        </div>
      </section>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statValue}>{Number(data.total_referrals) || 0}</span>
          <span className={styles.statLabel}>Amigos registrados</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{Number(data.active_referrals) || 0}</span>
          <span className={styles.statLabel}>Con primera recarga</span>
        </div>
        {isInfluencer ? (
          <div className={styles.stat}>
            <span className={styles.statValue}>{formatCurrency(data.total_earnings)}</span>
            <span className={styles.statLabel}>Comisiones ganadas</span>
          </div>
        ) : (
          <div className={styles.stat}>
            <span className={styles.statValue}>{earned}</span>
            <span className={styles.statLabel}>
              Fracciones gratis ganadas
              {rewards.available != null && earned > 0
                ? ` (${availableRewards} disponibles, ${usedRewards} usadas)`
                : ""}
            </span>
          </div>
        )}
      </div>

      <section aria-labelledby="referidos-lista">
        <h3 id="referidos-lista" className={styles.sectionTitle}>
          Tus referidos
        </h3>
        {referrals.length === 0 ? (
          <EmptyState
            icon={<FontAwesomeIcon icon={faUserGroup} />}
            title="Aún no tienes referidos"
            description="Comparte tu código con tus amigos y familiares para empezar a ganar."
          />
        ) : (
          <ul className={styles.list}>
            {referrals.map((r, i) => {
              const rewardText =
                r.reward === "FRACTION"
                  ? "Ganaste 1 fracción gratis"
                  : r.reward === "COMMISSION"
                    ? "Comisión acreditada"
                    : "Esperando su primera recarga";
              return (
                <li key={`${r.user_name}-${r.date}-${i}`} className={styles.item}>
                  <div className={styles.itemInfo}>
                    <span className={styles.itemName}>{r.user_name || "Usuario"}</span>
                    <span className={styles.itemMeta}>
                      {r.date ? `Se registró el ${formatBonusLongDate(r.date)} · ` : ""}
                      {rewardText}
                    </span>
                  </div>
                  <Badge variant={REFERRAL_STATUS_VARIANT[String(r.status || "").toUpperCase()] || "default"}>
                    {r.status_display || r.status || "—"}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
